import type { ReactNode } from 'react';

interface OptionCardProps {
  selected: boolean;
  onSelect: () => void;
  testId: string;
  children: ReactNode;
}

/**
 * One of the two residency choices. A real `<button>` with `role="radio"`, so
 * the attestation is operable by keyboard and announced by screen readers —
 * this is a legal declaration, not a styling exercise.
 */
export default function OptionCard({
  selected,
  onSelect,
  testId,
  children,
}: OptionCardProps) {
  // The selected/unselected outline is a ring, not a border: a border changing
  // width between the two states resizes the content box and nudges the label.
  // `ring-inset` paints inside the box, so selecting an option never shifts the
  // layout.
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      data-testid={testId}
      onClick={onSelect}
      className={`flex min-h-[68px] w-full items-center gap-3.5 rounded-2xl px-[18px] py-4 text-left transition-all ${
        selected
          ? 'bg-[#569DE5]/[0.06] shadow-[0_4px_14px_rgba(86,157,229,0.13)] ring-2 ring-inset ring-[#569DE5]'
          : 'bg-white ring-[1.5px] ring-inset ring-[#e2e4e8]'
      }`}
    >
      <span
        className={`box-border size-6 shrink-0 rounded-full bg-white ${
          selected ? 'border-[7px] border-[#569DE5]' : 'border-2 border-[#c4c8ce]'
        }`}
      />
      <span
        className={`text-base font-semibold leading-[1.3] ${
          selected ? 'text-[#16171a]' : 'text-[#33373e]'
        }`}
      >
        {children}
      </span>
    </button>
  );
}
